import { t } from "./i18n";

// Chromium's install prompt event. Not in the DOM lib typings.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallEnv {
  // Already launched from the home screen: nothing to offer.
  isStandalone: boolean;
  // iOS has no install prompt API; users add the app through the Share menu.
  isIos: boolean;
  // Where beforeinstallprompt and appinstalled fire (window in the browser).
  events: EventTarget;
}

export function detectInstallEnv(): InstallEnv {
  const nav = navigator as Navigator & { standalone?: boolean };
  // iPadOS reports itself as a Mac; touch support tells them apart.
  const isIos =
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  return {
    isStandalone: window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true,
    isIos,
    events: window,
  };
}

const SVG_NS = "http://www.w3.org/2000/svg";

// Outline icons on a 24px grid, drawn in currentColor.
const ICONS = {
  install: ["M12 3v12", "M7 10l5 5 5-5", "M5 19h14"],
  share: ["M12 3v12", "M8 7l4-4 4 4", "M8 11H6v10h12V11h-2"],
  add: ["M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", "M12 8v8", "M8 12h8"],
  confirm: ["M5 12l5 5 9-10"],
};

function icon(name: keyof typeof ICONS): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("class", "install-icon");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  for (const d of ICONS[name]) {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);
  }
  return svg;
}

function createButton(): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "install-btn";
  btn.appendChild(icon("install"));
  btn.append(t("ui.install.button"));
  return btn;
}

function createIosSteps(onClose: () => void): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "install-dialog";
  dialog.setAttribute("aria-labelledby", "install-dialog-title");

  const heading = document.createElement("h2");
  heading.id = "install-dialog-title";
  heading.textContent = t("ui.install.ios.title");
  dialog.appendChild(heading);

  const steps = document.createElement("ol");
  const stepList: Array<[keyof typeof ICONS, string]> = [
    ["share", "ui.install.ios.step_share"],
    ["add", "ui.install.ios.step_add"],
    ["confirm", "ui.install.ios.step_confirm"],
  ];
  for (const [glyph, key] of stepList) {
    const li = document.createElement("li");
    li.appendChild(icon(glyph));
    const text = document.createElement("span");
    text.textContent = t(key);
    li.appendChild(text);
    steps.appendChild(li);
  }
  dialog.appendChild(steps);

  const close = document.createElement("button");
  close.type = "button";
  close.className = "btn-secondary install-close";
  close.textContent = t("ui.install.close");
  close.addEventListener("click", () => dialog.close());
  dialog.appendChild(close);

  dialog.addEventListener("close", onClose);
  return dialog;
}

// Offers home-screen installation from a fixed spot. Never prompts on its own: on Chromium
// the browser's banner is suppressed and the native dialog opens only from our button; on
// iOS the button opens the manual Share-menu steps.
export function mountInstallButton(container: HTMLElement, env: InstallEnv = detectInstallEnv()): void {
  if (env.isStandalone) return;

  if (env.isIos) {
    const btn = createButton();
    const dialog = createIosSteps(() => btn.focus());
    btn.addEventListener("click", () => dialog.showModal());
    container.append(btn, dialog);
    return;
  }

  let btn: HTMLButtonElement | null = null;

  env.events.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    const deferred = e as BeforeInstallPromptEvent;
    btn?.remove();
    btn = createButton();
    const current = btn;
    current.addEventListener("click", async () => {
      current.disabled = true;
      await deferred.prompt();
      await deferred.userChoice;
      // A prompt event can only be used once; the browser fires a fresh one if installing
      // becomes possible again.
      current.remove();
    });
    container.appendChild(current);
  });

  env.events.addEventListener("appinstalled", () => btn?.remove());
}
